import { useEffect, useState } from 'react';
import { api } from '@shared/lib/api';

interface BalanceAmount {
  amount: number;
  currency: string;
}

interface StripeBalance {
  available: BalanceAmount[];
  pending: BalanceAmount[];
}

interface Props {
  apiKey: string;
}

export default function StripeBalanceCards({ apiKey }: Props) {
  const [balance, setBalance] = useState<StripeBalance | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!apiKey) return;
    api.stripeBalance(apiKey)
      .then((data) => setBalance(data as StripeBalance))
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [apiKey]);

  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="rounded p-4 animate-pulse"
            style={{ border: '1px solid var(--dash-border)', background: 'var(--dash-card)' }}
          >
            <div className="h-3 w-24 rounded mb-3" style={{ background: 'var(--dash-hover-bg)' }} />
            <div className="h-6 w-20 rounded" style={{ background: 'var(--dash-hover-bg)' }} />
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="rounded p-4 mb-6"
        style={{ border: '1px solid var(--dash-border)', background: 'var(--dash-card)' }}
      >
        <p className="font-sans text-sm" style={{ color: 'var(--dash-text-muted)' }}>{error}</p>
      </div>
    );
  }

  const available = balance?.available?.[0]?.amount ?? 0;
  const pending = balance?.pending?.[0]?.amount ?? 0;
  const availableDisplay = (available / 100).toFixed(2);
  const pendingDisplay = (pending / 100).toFixed(2);

  let nextPayout = 'No pending funds';
  if (pending > 0) nextPayout = '~2 business days';
  else if (available > 0) nextPayout = 'Processing';

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
      <div
        className="rounded p-4"
        style={{ border: '1px solid var(--dash-border)', background: 'var(--dash-card)' }}
      >
        <div
          className="font-mono text-[10px] uppercase tracking-widest mb-1.5"
          style={{ color: 'var(--dash-text-dim)' }}
        >
          Available Balance
        </div>
        <div className="font-mono text-xl text-emerald-400">${availableDisplay}</div>
      </div>
      <div
        className="rounded p-4"
        style={{ border: '1px solid var(--dash-border)', background: 'var(--dash-card)' }}
      >
        <div
          className="font-mono text-[10px] uppercase tracking-widest mb-1.5"
          style={{ color: 'var(--dash-text-dim)' }}
        >
          Pending Balance
        </div>
        <div className="font-mono text-xl text-amber-400">${pendingDisplay}</div>
      </div>
      <div
        className="rounded p-4"
        style={{ border: '1px solid var(--dash-border)', background: 'var(--dash-card)' }}
      >
        <div
          className="font-mono text-[10px] uppercase tracking-widest mb-1.5"
          style={{ color: 'var(--dash-text-dim)' }}
        >
          Next Payout
        </div>
        <div className="font-mono text-xl" style={{ color: 'var(--dash-text-primary)' }}>
          {nextPayout}
        </div>
      </div>
    </div>
  );
}
